"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.RuleHandler = void 0;
const url_pattern_1 = __importDefault(require("url-pattern"));
const lib_1 = require("../slotContainers/lib");
class RuleHandler {
    constructor({ ruleSets, type, widgets, item, injectionType = 'incontent' }) {
        this.ruleSets = { default: [] };
        this.type = type;
        this.item = item;
        this.injectionType = injectionType;
        this.widgets = this.filterWidgetsByType(widgets, injectionType);
        this.ruleSets = Object.entries(ruleSets).reduce((acc, [key, ruleList]) => {
            acc[key] = this.filterRuleSetsByType(ruleList, this.widgets, injectionType);
            return acc;
        }, {});
    }
    isNextTo(cssSelector, injectionPointElement) {
        return ((injectionPointElement.is(cssSelector) ||
            (injectionPointElement[0].next && injectionPointElement.next().is(cssSelector))) ??
            false);
    }
    notNextTo(nextToConditions, injectionPointElement) {
        return nextToConditions?.every((cssSelector) => !this.isNextTo(cssSelector, injectionPointElement));
    }
    /**
     * Returns true if the injection point is not a
     * child of any of the provided CSS selectors.
     *
     * N/A for standalone slot-type
     **/
    isNotWithin(injectionPointElement, cssSelectors = [], slotType) {
        if (slotType === 'standalone') {
            return false;
        }
        if (cssSelectors.length === 0) {
            return false;
        }
        // Element.closest() is not supported in this version of cheerio
        function closest(element, cssSelector) {
            let parent = element.parent();
            // Traverse up the DOM tree to find the parent selector
            while (parent.length > 0) {
                if (parent.is(cssSelector)) {
                    return true;
                }
                parent = parent.parent();
            }
            return false;
        }
        return cssSelectors.every((cssSelector) => {
            return closest(injectionPointElement, cssSelector);
        });
    }
    failsItemRule(itemConditions, not) {
        if (!itemConditions && !not) {
            return false;
        }
        if (not?.item) {
            return Object.entries(not?.item).every(([key, value]) => {
                return this.item?.[key] === value;
            });
        }
        return !Object.entries(itemConditions || []).every(([key, value]) => {
            return this.item?.[key] === value;
        });
    }
    /**
     * Returns true if the rule is specified and current
     * index doesn't match specified index.
     *
     * N/A for standalone slot-type
     **/
    failsIndexRule(index, specifiedIndex, slotType) {
        if (slotType === 'standalone') {
            return false;
        }
        if (specifiedIndex !== undefined && specifiedIndex !== index) {
            return true;
        }
        return false;
    }
    /**
     * Returns true if the rule is specified and current
     * density/content-size doesn't match specified density.
     **/
    failsDensityRule(contentSize, density) {
        if (density !== undefined && contentSize !== density) {
            return true;
        }
        return false;
    }
    /**
     * Returns true in two situations:
     * 1. If next_to is specified and the CSS selector(s)
     *    do not match the element.
     *
     * 2. If not.next_to is specified and the CSS selector(s)
     *    match the pattern supplied by not.href_matches
     *
     * N/A for standalone slot-type
     **/
    failsNextToRule(injectionPointElement, next_to, not, slotType) {
        if (slotType === 'standalone') {
            return false;
        }
        if (next_to !== undefined && next_to.some((css) => !this.isNextTo(css, injectionPointElement))) {
            return true;
        }
        if (not?.next_to !== undefined &&
            not.next_to.some((css) => this.isNextTo(css, injectionPointElement))) {
            return true;
        }
        return false;
    }
    /**
     * Returns true in two situations:
     * 1. If href_matches is specified and the current path
     *    does not match the pattern supplied by href_matches
     *
     * 2. If not.href_matches is specified and the current path
     *    matches the pattern supplied by not.href_matches
     **/
    failsHrefMatchesRule(path, href_matches, not) {
        if (href_matches !== undefined) {
            // Pattern must match.
            const pattern = new url_pattern_1.default(href_matches);
            // eslint-disable-next-line  @typescript-eslint/strict-boolean-expressions
            if (!pattern.match(path)) {
                return true;
            }
        }
        if (not?.href_matches !== undefined) {
            // Pattern must NOT match.
            const pattern = new url_pattern_1.default(not.href_matches);
            // eslint-disable-next-line  @typescript-eslint/strict-boolean-expressions
            if (pattern.match(path)) {
                return true;
            }
        }
        return false;
    }
    evaluateAndConditions(and, index, contentSize, injectionPointElement, path, slotType) {
        return and.every(({ index_is, density, not, next_to, href_matches, item }) => {
            return [
                this.failsIndexRule(index, index_is, slotType),
                this.failsDensityRule(contentSize, density),
                this.failsNextToRule(injectionPointElement, next_to, not, slotType),
                this.failsHrefMatchesRule(path, href_matches, not),
                this.isNotWithin(injectionPointElement, not?.within, slotType),
                this.failsItemRule(item, not),
            ].every((failed) => !failed);
        });
    }
    evaluateRules({ index, contentSize, injectionPointElement, path, variant, }) {
        const rules = [];
        const allRuleSets = Object.values(this.ruleSets).flat();
        for (const rule of allRuleSets) {
            const widget = this.widgets[rule.variant];
            if ((variant !== undefined && rule.variant !== variant) || !(0, lib_1.isDefined)(widget)) {
                continue;
            }
            for (const { and } of rule.where) {
                if (this.evaluateAndConditions(and, index, contentSize, injectionPointElement, path, widget.slotType)) {
                    rules.push(rule);
                }
            }
        }
        return rules.map((rule) => {
            const { config, slotType, widgetType } = this.widgets[rule.variant];
            return {
                name: rule.variant,
                config,
                slotType,
                widgetType,
            };
        });
    }
    // Filters ruleSets so that the type of rule matches the injection type.
    // "incontent" is the default for both rules and injection type.
    filterRuleSetsByType(ruleSets, widgets, injectionType) {
        const filteredRuleSets = [];
        for (const rule of ruleSets) {
            const widget = widgets[rule.variant];
            if (!(0, lib_1.isDefined)(widget)) {
                continue;
            }
            if ((widget.slotType ?? 'incontent') === injectionType) {
                filteredRuleSets.push(rule);
            }
        }
        return filteredRuleSets;
    }
    // Filters widget so that the type of widget matches the injection type.
    // "incontent" is the default for both widgets and injection type.
    filterWidgetsByType(widgets, injectionType) {
        const filteredWidgets = {};
        for (const variantName in widgets) {
            if ((widgets[variantName]?.slotType ?? 'incontent') === injectionType) {
                filteredWidgets[variantName] = widgets[variantName];
            }
        }
        return filteredWidgets;
    }
}
exports.RuleHandler = RuleHandler;
//# sourceMappingURL=ruleHandler.js.map